import { UpdateIncomeDetails } from '@/app-storage/applications/familyApplicationSlice'
import { useAppAlert } from '@/app/AppAlertProvider'
import { MinusIcon, PlusIcon } from '@/app/common/icons/arrows'
import { cn } from '@/common/utilities.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { AppDispatch, RootState } from '@/app-storage/store'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { SubmitButton } from '../student_details/shared/submit-button'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { selectProgramID } from '@/app-storage/program_settings/programSettingsSlice.ts'
import { useTranslation } from 'react-i18next'

// Define the form schema
const formSchema = z.object({
  householdSize: z
    .number()
    .min(1, 'please_add_at_least_one')
    .max(99, 'invalid_household_size'),
})

type FormValues = z.infer<typeof formSchema>

const DEFAULT_HOUESHOLD_SIZE = 2

export const HouseholdSizePage = () => {
  const { t: translate } = useTranslation()
  const ProgramId = useSelector(selectProgramID)
  const dispatch = useDispatch<AppDispatch>()
  const navigateTo = useNavigate()
  const { fireAlert } = useAppAlert()

  const { data: applicationData, request } = useSelector(
    (state: RootState) => state.FamilyApplication
  )

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      householdSize:
        applicationData?.household_inhabitants ?? DEFAULT_HOUESHOLD_SIZE,
    },
  })

  const currentSize = watch('householdSize')

  const onSubmit = async (data: FormValues) => {
    const resultAction = await dispatch(
      UpdateIncomeDetails({
        household_inhabitants: data.householdSize,
        program_id: ProgramId,
      })
    )

    if (UpdateIncomeDetails.fulfilled.match(resultAction)) {
      navigateTo(
        AppRoutePaths.family_application.income_verification.filing_status
      )
    } else {
      fireAlert({
        variant: 'error',
        message: resultAction.payload?.message,
      })
    }
  }

  const decrementSize = () => {
    if (currentSize > 1) {
      setValue('householdSize', currentSize - 1)
    }
  }

  const incrementSize = () => {
    setValue('householdSize', currentSize + 1)
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('household_size')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content>
        <section
          id="income-verification"
          aria-labelledby="income-verification-title"
        >
          <h2
            id="income-verification-title"
            className="text-xl font-medium text-gray-900"
          >
            {translate('how_big_is_your_household')}{' '}
          </h2>

          <p className="mt-4 text-gray-700 text-sm">
            {translate('how_big_is_your_household_text')}.
          </p>

          <form
            id="household-size-form"
            onSubmit={handleSubmit(onSubmit)}
            className="mt-16 flex-grow flex flex-col"
            aria-labelledby="household-size-label"
          >
            <div
              className="flex justify-between items-center"
              role="group"
              aria-labelledby="household-size-label"
            >
              <span id="household-size-label" className="sr-only">
                {translate('household_size')}
              </span>
              <button
                type="button"
                onClick={decrementSize}
                className={cn(
                  'flex justify-center items-center rounded-full text-3xl h-10 w-10 bg-primary text-white',
                  currentSize > 1
                    ? 'cursor-pointer'
                    : 'opacity-50 cursor-not-allowed'
                )}
                aria-label={translate('decrease_household_size')}
                aria-controls="household-size-value"
                disabled={currentSize <= 1}
              >
                <MinusIcon aria-hidden="true" />
              </button>
              <div className="text-5xl" aria-live="polite" aria-atomic="true">
                <input
                  type="hidden"
                  {...register('householdSize', { valueAsNumber: true })}
                />
                {currentSize}
              </div>
              <button
                type="button"
                onClick={incrementSize}
                className="flex justify-center items-center rounded-full text-3xl h-10 w-10 bg-primary text-white cursor-pointer"
                aria-label={translate('increase_household_size')}
                aria-controls="household-size-value"
              >
                <PlusIcon aria-hidden="true" />
              </button>
            </div>
            {errors.householdSize && (
              <p className="text-red-500 text-sm mt-2" role="alert">
                {translate(errors.householdSize.message ?? '')}
              </p>
            )}
          </form>
        </section>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <SubmitButton status={request.status} form="household-size-form" />
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
